import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { ColumnFilterType } from "shared/cubiq-services";

import { UNSET_FILTER_OPERATOR, BfxFindFilterCondition } from "./models/BfxFindFilterCondition";
import { BfxFindFilterOperatorSet } from "./models/BfxFindFilterOperatorSet";
import { BfxFindFilterConditionLabels } from "./models/BfxFindFilterConditionLabels";
import { EvalNumberEnum } from "../shared/typescript/EvalNumberEnum";
import { DataTable } from "@bfx/primeng/datatable";
import { BfxFindFilterHeader } from "./models/BfxFindFilterHeader";
import { MULTI_SELECTOR_LIMIT } from "../bfx-element-library/bfx-multi-selector.constants";
import { LabelValue } from "shared/cubiq-services/model/core/AutoCompleteLabelValue";
import {
  BFX_FIND_TRADE_CLIENT_VALUE,
  BFX_FIND_TRADE_RISK_VALUE,
} from "shared/cubiq-services/model/find/bfx-find-deals/bfx-find-deals.constants";

export class FilterData implements BfxFindFilterHeader {
  static templateId = -1;

  id: number;
  label: string;
  filterConditions: BfxFindFilterCondition[];
  expandedIndex: number;
  isRowExpanded: boolean;

  dt?: DataTable;
}

export const BFX_FIND_OPERATOR_DATA = new Map([
  [
    BfxFindFilterOperatorSet.Equal,
    [UNSET_FILTER_OPERATOR, BfxFindFilterConditionLabels[CubiqTS.Operator.Equal]],
  ],
  [
    BfxFindFilterOperatorSet.EqualNotEqual,
    [
      UNSET_FILTER_OPERATOR,
      BfxFindFilterConditionLabels[CubiqTS.Operator.Equal],
      BfxFindFilterConditionLabels[CubiqTS.Operator.NotEqual],
    ],
  ],
  [
    BfxFindFilterOperatorSet.InNotIn,
    [
      UNSET_FILTER_OPERATOR,
      BfxFindFilterConditionLabels[CubiqTS.Operator.In],
      BfxFindFilterConditionLabels[CubiqTS.Operator.NotIn],
    ],
  ],
  [
    BfxFindFilterOperatorSet.All,
    [
      UNSET_FILTER_OPERATOR,
      BfxFindFilterConditionLabels[CubiqTS.Operator.Equal],
      BfxFindFilterConditionLabels[CubiqTS.Operator.NotEqual],
      BfxFindFilterConditionLabels[CubiqTS.Operator.LessOrEqual],
      BfxFindFilterConditionLabels[CubiqTS.Operator.Less],
      BfxFindFilterConditionLabels[CubiqTS.Operator.Greater],
      BfxFindFilterConditionLabels[CubiqTS.Operator.GreaterOrEqual],
      BfxFindFilterConditionLabels[CubiqTS.Operator.Between],
    ],
  ],
  // OMS-34517 4.7.x temporary restriction on client id/name, account id/name and client trader
  [
    BfxFindFilterOperatorSet.In,
    [UNSET_FILTER_OPERATOR, BfxFindFilterConditionLabels[CubiqTS.Operator.In]],
  ],
]);

export const ACCOUNT_FILTER_TYPES: Array<ColumnFilterType> = [
  ColumnFilterType.ACCOUNT,
  ColumnFilterType.ACCOUNTID,
];
export const CLIENT_FILTER_TYPES: Array<ColumnFilterType> = [
  ColumnFilterType.CLIENT,
  ColumnFilterType.CLIENTID,
];
export const SINGLE_FILTER_TYPES: Array<ColumnFilterType> =
  ACCOUNT_FILTER_TYPES.concat(CLIENT_FILTER_TYPES);

export const FIND_OBJECT_SORT: any = (a, b) => {
  return a.label.toLowerCase() < b.label.toLowerCase()
    ? -1
    : a.label.toLowerCase() > b.label.toLowerCase()
    ? 1
    : 0;
};

export const BfxFindOrderStateLive = EvalNumberEnum({
  PENDING: CubiqTS.OrderState.PENDING,
  SI_REQUIRED: CubiqTS.FxLifeCycleEventType.SI_REQUIRED,
  IN_MODIFICATION: CubiqTS.OrderState.IN_MODIFICATION,
  ACCEPTANCE_NEEDED: CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  ACTIVE: CubiqTS.OrderState.ACTIVE,
  CANCELLED: CubiqTS.OrderState.CANCELLED,
  PENDING_CANCELLATION: CubiqTS.OrderState.PENDING_CANCELLATION,
  CANCEL_ACCEPTANCE_NEEDED: CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED,
  MODIFY_ACCEPTANCE_NEEDED: CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  FILLED: CubiqTS.OrderState.FILLED,
  EXPIRED: CubiqTS.OrderState.EXPIRED,
  REJECTED: CubiqTS.OrderState.REJECTED,
  TRIGGERED: CubiqTS.OrderState.TRIGGERED,
  CREDIT_CHECKING: CubiqTS.OrderState.CREDIT_CHECKING,
  // GAMMA_MID_PENDING: CubiqTS.OrderState. ,
  MIGRATION_ACCEPTANCE_NEEDED: CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED,
  SUSPENDED: CubiqTS.OrderState.SUSPENDED,
  EXPIRY_ACCEPTANCE_NEEDED: CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED,
  FILLING: CubiqTS.OrderState.FILLING,
  PENDING_FWDS: CubiqTS.OrderState.PENDING_FWDS,
  EXPIRY_PENDING_FWDS: CubiqTS.OrderState.EXPIRY_PENDING_FWDS,
  CANCEL_PENDING_FWDS: CubiqTS.OrderState.CANCEL_PENDING_FWDS,
});

export const BfxFindOrderFillState = EvalNumberEnum({
  FILL_ERROR: CubiqTS.OrderState.FILL_ERROR,
  FILL_EXPIRED: CubiqTS.OrderState.FILL_EXPIRED,
});

export const BfxFindOrderStateNonLive = EvalNumberEnum({
  CANCELLED: CubiqTS.OrderState.CANCELLED,
  FILLED: CubiqTS.OrderState.FILLED,
  EXPIRED: CubiqTS.OrderState.EXPIRED,
  REJECTED: CubiqTS.OrderState.REJECTED,
});

export const BfxFindOrderDealState = EvalNumberEnum({
  ACTIVE: CubiqTS.OrderDealState.ACTIVE,
  FILL_ERROR: CubiqTS.OrderDealState.FILL_ERROR,
  COMPLETED: CubiqTS.OrderDealState.COMPLETED,
  CANCELLED: CubiqTS.OrderDealState.CANCELLED,
  ACK_TIME_OUT: CubiqTS.OrderDealState.ACK_TIME_OUT,
  NAK: CubiqTS.OrderDealState.NAK,
  DONE_NO_STP: CubiqTS.OrderDealState.DONE_NO_STP,
  STP_HOLD: CubiqTS.OrderDealState.STP_HOLD,
  PENDING: CubiqTS.OrderDealState.PENDING,
});

export const BFX_FIND_MULTI_SELECTOR_LIMIT = MULTI_SELECTOR_LIMIT;

export class TimezoneCdLabelValue implements LabelValue<boolean> {
  constructor(public label: string, public value: boolean) {}
}
export const BFX_FIND_TIMEZONE_CD_UTC = new TimezoneCdLabelValue("UTC", true);
export const BFX_FIND_TIMEZONE_CD_LOCAL = new TimezoneCdLabelValue("Local", false);
export const BFX_FIND_TIMEZONE_CODES = [BFX_FIND_TIMEZONE_CD_UTC, BFX_FIND_TIMEZONE_CD_LOCAL];

/** For Forward First drop down */
export class ForwardFirstLabelValue implements LabelValue<boolean> {
  constructor(public label: string, public value: boolean) {}
}
export const BFX_FIND_FORWARD_FIRST_TRUE = new ForwardFirstLabelValue("Forward First", true);
export const BFX_FORWARD_FIRST_FALSE = new ForwardFirstLabelValue("None", false);
export const BFX_FIND_FORWARD_FIRST_CODES = [BFX_FIND_FORWARD_FIRST_TRUE, BFX_FORWARD_FIRST_FALSE];

/* For Trade type drop down */
export class TradeTypeLabelValue implements LabelValue<string> {
  constructor(public label: string, public value: string) {}
}
export const BFX_FIND_TRADE_TYPE_CLIENT = new TradeTypeLabelValue(
  "Client Trades",
  BFX_FIND_TRADE_CLIENT_VALUE,
);
export const BFX_FIND_TRADE_TYPE_RISK = new TradeTypeLabelValue(
  "Risk Trades",
  BFX_FIND_TRADE_RISK_VALUE,
);
export const BFX_FIND_TRADE_TYPES = [BFX_FIND_TRADE_TYPE_CLIENT, BFX_FIND_TRADE_TYPE_RISK];
